import {
  AgentClientApplication,
  AiService,
  AgentClientApplicationIcon,
  FindingAiService,
  InLineControlAiService,
  InlineClientApplication,
} from '../models/api/agentStep.model';
import { RuleTag } from '../models/api/governance/violationRule.model';
import { beautifyCamelCaseString } from './stringUtils';
import { ResourceType } from '../models/api/common';

type AnyAiService = AiService | FindingAiService | InLineControlAiService;

// TODO: merge cases with the same logo
export function getAiServiceLogoPath(aiService: AnyAiService) {
  switch (aiService) {
    // AiService
    case AiService.m365Copilot:
      return 'assets/images/logo/m365Copilot.svg';
    case AiService.salesforceEinstein:
      return 'assets/images/logo/salesForce-icon.svg';
    case AiService.agentforce:
      return 'assets/images/logo/agentforce-logo.svg';
    case AiService.copilotStudio:
      return 'assets/images/logo/copilotStudio.svg';
    case AiService.chatGptEnterprise:
      return 'assets/images/logo/openAI-icon.svg';
    case AiService.azureAiFoundry:
      return 'assets/images/logo/azure-ai-foundry.svg';
    case AiService.vertexAi:
      return 'assets/images/logo/vertex-ai.svg';
    case AiService.geminiEnterpriseAgentDesigner:
      return 'assets/images/logo/gemini-enterprise-agent-designer.svg';
    case AiService.powerAutomate:
      return 'assets/images/logo/powerAutomateFlow.svg';
    case AiService.powerApps:
      return 'assets/images/logo/PowerApps.svg';
    case AiService.powerPages:
      return 'assets/images/logo/PowerPages.svg';
    case AiService.bedrock:
      return 'assets/images/logo/amazon-bedrock-logo.svg';
    case AiService.bedrockAgentCore:
      return 'assets/images/logo/amazon-bedrock-agentcore-logo.svg';
    case AiService.serviceNow:
      return 'assets/images/logo/serviceNow-logo.svg';
    case AiService.microsoft365:
      return 'assets/images/logo/m365Copilot.svg';
    case AiService.gitHub:
    // Handle backend inconsistency: 'githubCopilot' value
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore - Backend sends 'githubCopilot' but enum has 'gitHub'
    // falls through
    case 'githubCopilot':
      return 'assets/images/logo/Github-copilot-logo.svg';
    case AiService.slack:
      return 'assets/images/logo/slack-logo.svg';
    // Device-based AI services
    case AiService.claude:
      return 'assets/images/logo/claude-logo.svg';
    case AiService.perplexity:
      return 'assets/images/logo/perplexity-logo.svg';
    case AiService.agentKit:
      return 'assets/images/logo/openAI-agentBuilder-logo.svg';
    case AiService.cursor:
      return 'assets/images/logo/cursor-logo.svg';
    case AiService.windsurf:
      return 'assets/images/logo/windsurf-logo.svg';
    case AiService.tabnine:
      return 'assets/images/logo/tabnine-logo.svg';
    case AiService.amazonQ:
      return 'assets/images/logo/amazon-q-logo.svg';
    case AiService.dia:
      return 'assets/images/logo/dia-logo.svg';
    case AiService.claudeCode:
      return 'assets/images/logo/claude-logo.svg';
    case AiService.homegrown:
      return 'assets/images/logo/agent-client-app-other.svg';
    case AiService.customAgent:
      return 'assets/images/logo/agent-client-app-other.svg';
    case AiService.langchain:
      return 'assets/images/logo/langchain.svg';
    case AiService.openaiCodex:
      return 'assets/images/logo/openAI-codex-icon.svg';

    // FindingAiService
    case FindingAiService.serviceNow:
      return 'assets/images/logo/serviceNow-logo.svg';
    case FindingAiService.sharepointAgents:
      return 'assets/images/logo/agent-client-app-sharepoint.svg';
    case FindingAiService.claudeCode:
      return 'assets/images/logo/claude-logo.svg';
    case FindingAiService.homegrown:
      return 'assets/images/logo/agent-client-app-other.svg';
    case FindingAiService.customAgent:
      return 'assets/images/logo/unknown-bot.svg';
    case FindingAiService.langchain:
      return 'assets/images/logo/langchain.svg';
    case FindingAiService.openaiCodex:
      return 'assets/images/logo/openAI-codex-icon.svg';

    // InLineControlAiService
    case InLineControlAiService.gitHub:
      return 'assets/images/logo/Github-copilot-logo.svg';
    case InLineControlAiService.slack:
      return 'assets/images/logo/slack-logo.svg';
    case InLineControlAiService.m365Copilot:
      return 'assets/images/logo/m365Copilot.svg';
    case InLineControlAiService.azureAiFoundry:
      return 'assets/images/logo/azure-ai-foundry.svg';
    case InLineControlAiService.chatGptEnterprise:
      return 'assets/images/logo/openAI-icon.svg';
    case InLineControlAiService.claude:
      return 'assets/images/logo/claude-logo.svg';
    case InLineControlAiService.copilotStudio:
      return 'assets/images/logo/copilotStudio.svg';
    case InLineControlAiService.agentKit:
      return 'assets/images/logo/openAI-agentBuilder-logo.svg';
    case InLineControlAiService.perplexity:
      return 'assets/images/logo/perplexity-logo.svg';
    case InLineControlAiService.cursor:
      return 'assets/images/logo/cursor-logo.svg';
    case InLineControlAiService.unknown:
      return 'assets/images/logo/unknown-bot.svg';
    default:
      console.warn(`getAiServiceLogoPath: unknown platform "${aiService}", using fallback icon`);
      return 'assets/images/logo/unknown-bot.svg';
  }
}

const AI_SERVICE_DISPLAY_NAMES: Record<AiService, string> = {
  [AiService.m365Copilot]: 'M365 Copilot',
  [AiService.salesforceEinstein]: 'Salesforce Einstein',
  [AiService.agentforce]: 'Agentforce',
  [AiService.copilotStudio]: 'Copilot Studio',
  [AiService.chatGptEnterprise]: 'ChatGPT Enterprise',
  [AiService.azureAiFoundry]: 'Azure AI Foundry',
  [AiService.vertexAi]: 'Vertex AI',
  [AiService.geminiEnterpriseAgentDesigner]: 'Gemini Enterprise',
  [AiService.serviceNow]: 'ServiceNow',
  [AiService.powerAutomate]: 'Power Automate',
  [AiService.powerApps]: 'Power Apps',
  [AiService.powerPages]: 'Power Pages',
  [AiService.bedrock]: 'Bedrock',
  [AiService.bedrockAgentCore]: 'Bedrock AgentCore',
  [AiService.microsoft365]: 'M365',
  [AiService.gitHub]: 'GitHub Copilot',
  [AiService.slack]: 'Slack',
  // Device-based AI services
  [AiService.claude]: 'Claude',
  [AiService.perplexity]: 'Perplexity',
  [AiService.cursor]: 'Cursor',
  [AiService.windsurf]: 'Windsurf',
  [AiService.tabnine]: 'Tabnine',
  [AiService.amazonQ]: 'Amazon Q',
  [AiService.dia]: 'Dia',
  [AiService.agentKit]: 'AgentKit',
  [AiService.claudeCode]: 'Claude Code',
  [AiService.homegrown]: 'Homegrown',
  [AiService.customAgent]: 'Custom Agent',
  [AiService.langchain]: 'LangChain',
  [AiService.openaiCodex]: 'Codex',
};

const FINDING_AI_SERVICE_DISPLAY_NAMES: Record<FindingAiService, string> = {
  [FindingAiService.m365Copilot]: 'M365 Copilot',
  [FindingAiService.salesforceEinstein]: 'Salesforce Einstein',
  [FindingAiService.agentforce]: 'Agentforce',
  [FindingAiService.copilotStudio]: 'Copilot Studio',
  [FindingAiService.chatGptEnterprise]: 'ChatGPT Enterprise',
  [FindingAiService.azureAiFoundry]: 'Azure AI Foundry',
  [FindingAiService.vertexAi]: 'Vertex AI',
  [FindingAiService.powerAutomate]: 'Power Automate',
  [FindingAiService.powerApps]: 'Power Apps',
  [FindingAiService.powerPages]: 'Power Pages',
  [FindingAiService.bedrock]: 'Bedrock',
  [FindingAiService.serviceNow]: 'ServiceNow',
  [FindingAiService.sharepointAgents]: 'SharePoint Agents',
  [FindingAiService.bedrockAgentCore]: 'Bedrock AgentCore',
  [FindingAiService.claudeCode]: 'Claude Code',
  [FindingAiService.homegrown]: 'Homegrown',
  [FindingAiService.customAgent]: 'Custom Agent',
  [FindingAiService.langchain]: 'LangChain',
  [FindingAiService.openaiCodex]: 'Codex',
};

const IN_LINE_CONTROL_AI_SERVICE_DISPLAY_NAMES: Record<InLineControlAiService, string> = {
  [InLineControlAiService.gitHub]: 'GitHub Copilot',
  [InLineControlAiService.slack]: 'Slack',
  [InLineControlAiService.m365Copilot]: 'M365 Copilot',
  [InLineControlAiService.azureAiFoundry]: 'Azure AI Foundry',
  [InLineControlAiService.chatGptEnterprise]: 'ChatGPT Enterprise',
  [InLineControlAiService.claude]: 'Claude',
  [InLineControlAiService.copilotStudio]: 'Copilot Studio',
  [InLineControlAiService.agentKit]: 'AgentKit',
  [InLineControlAiService.perplexity]: 'Perplexity',
  [InLineControlAiService.cursor]: 'Cursor',
  [InLineControlAiService.unknown]: 'Unknown',
};

const INLINE_CLIENT_APPLICATION_DISPLAY_NAMES: Record<InlineClientApplication, string> = {
  [InlineClientApplication.copilotStudio]: 'Copilot Studio',
  [InlineClientApplication.chatGpt]: 'ChatGPT',
  [InlineClientApplication.gitHubCopilotVisualStudio]: 'GitHub Copilot (Visual Studio)',
  [InlineClientApplication.claudeDesktop]: 'Claude Desktop',
  [InlineClientApplication.copilot]: 'Copilot',
  [InlineClientApplication.perplexity]: 'Perplexity',
  [InlineClientApplication.gitHubCopilotWeb]: 'GitHub Copilot (Web)',
  [InlineClientApplication.cursor]: 'Cursor',
  [InlineClientApplication.chatKit]: 'ChatKit',
  [InlineClientApplication.claudeCode]: 'Claude Code',
  [InlineClientApplication.slack]: 'Slack',
};

const AI_SERVICE_VALUES = Object.values(AiService) as readonly AiService[];
const FINDING_AI_SERVICE_VALUES = Object.values(FindingAiService) as readonly FindingAiService[];
const IN_LINE_CONTROL_AI_SERVICE_VALUES = Object.values(InLineControlAiService) as readonly InLineControlAiService[];

function isAiService(value: AnyAiService): value is AiService {
  return (AI_SERVICE_VALUES as readonly string[]).includes(value as unknown as string);
}

function isFindingAiService(value: AnyAiService): value is FindingAiService {
  return (FINDING_AI_SERVICE_VALUES as readonly string[]).includes(value as unknown as string);
}

function isInLineControlAiService(value: AnyAiService): value is InLineControlAiService {
  return (IN_LINE_CONTROL_AI_SERVICE_VALUES as readonly string[]).includes(value as unknown as string);
}

export function getAiServiceDisplayName(aiService: AnyAiService): string {
  if (isAiService(aiService)) {
    const displayName = AI_SERVICE_DISPLAY_NAMES[aiService];
    if (displayName) return displayName;
  } else if (isFindingAiService(aiService)) {
    const displayName = FINDING_AI_SERVICE_DISPLAY_NAMES[aiService];
    if (displayName) return displayName;
  } else if (isInLineControlAiService(aiService)) {
    const displayName = IN_LINE_CONTROL_AI_SERVICE_DISPLAY_NAMES[aiService];
    if (displayName) return displayName;
  }
  throw new Error(`getAiServiceDisplayName: service ${aiService} not supported`);
}

export function getInlineClientApplicationDisplayName(clientApplication: InlineClientApplication): string {
  const displayName = INLINE_CLIENT_APPLICATION_DISPLAY_NAMES[clientApplication];
  if (displayName) return displayName;
  throw new Error(`getInlineClientApplicationDisplayName: client application ${clientApplication} not supported`);
}

export function getInlineClientApplicationLogoPath(clientApplication: InlineClientApplication): string {
  switch (clientApplication) {
    case InlineClientApplication.copilotStudio:
      return 'assets/images/logo/copilotStudio.svg';
    case InlineClientApplication.chatGpt:
      return 'assets/images/logo/openAI-icon.svg';
    case InlineClientApplication.gitHubCopilotVisualStudio:
    case InlineClientApplication.gitHubCopilotWeb:
      return 'assets/images/logo/Github-copilot-logo.svg';
    case InlineClientApplication.claudeDesktop:
    case InlineClientApplication.claudeCode:
      return 'assets/images/logo/claude-logo.svg';
    case InlineClientApplication.copilot:
      return 'assets/images/logo/m365Copilot.svg';
    case InlineClientApplication.perplexity:
      return 'assets/images/logo/perplexity-logo.svg';
    case InlineClientApplication.cursor:
      return 'assets/images/logo/cursor-logo.svg';
    case InlineClientApplication.chatKit:
      return 'assets/images/logo/openAI-agentBuilder-logo.svg';
    case InlineClientApplication.slack:
      return 'assets/images/logo/slack-logo.svg';
    default:
      return 'assets/images/logo/unknown-bot.svg';
  }
}

/**
 * Maps each InlineClientApplication to the exact backend
 * string(s) used in search/filter requests.
 */
export const INLINE_CLIENT_APPLICATION_BACKEND_VALUES: Readonly<Record<InlineClientApplication, readonly string[]>> = {
  [InlineClientApplication.copilotStudio]: ['Copilot Studio'],
  [InlineClientApplication.chatGpt]: ['chatGpt'],
  [InlineClientApplication.gitHubCopilotVisualStudio]: ['GitHub Copilot (Visual Studio)'],
  [InlineClientApplication.claudeDesktop]: ['Claude Desktop'],
  [InlineClientApplication.copilot]: ['Copilot'],
  [InlineClientApplication.perplexity]: ['Perplexity'],
  [InlineClientApplication.gitHubCopilotWeb]: ['GitHub Copilot (Web)'],
  [InlineClientApplication.cursor]: ['Cursor'],
  [InlineClientApplication.chatKit]: ['ChatKit'],
  [InlineClientApplication.claudeCode]: ['Claude Code'],
  [InlineClientApplication.slack]: ['Slack'],
};

export function getInlineClientApplicationBackendValues(clientApplication: InlineClientApplication): readonly string[] {
  return INLINE_CLIENT_APPLICATION_BACKEND_VALUES[clientApplication];
}

const INLINE_CLIENT_APPLICATION_DISPLAY_NAME_TO_ENUM = new Map<string, InlineClientApplication>(
  Object.entries(INLINE_CLIENT_APPLICATION_DISPLAY_NAMES).map(([enumValue, displayName]) => [
    displayName,
    enumValue as InlineClientApplication,
  ])
);

const INLINE_CLIENT_APPLICATION_VALUES = new Set<string>(Object.values(InlineClientApplication));

const AGENT_CLIENT_APPLICATION_ICON_VALUES = new Set<string>(Object.values(AgentClientApplicationIcon));

const CODEX_CLIENT_APPLICATION_DISPLAY_NAMES: Record<string, string> = {
  [AgentClientApplication.CODEX_CLI]: 'Codex CLI',
  [AgentClientApplication.CODEX_WEB]: 'Codex Web',
  [AgentClientApplication.CODEX_IDE_VSCODE]: 'Codex IDE (VS Code)',
  [AgentClientApplication.CODEX_GITHUB_ACTION]: 'Codex GitHub Action',
  [AgentClientApplication.CODEX_SDK_TS]: 'Codex SDK (TypeScript)',
  [AgentClientApplication.CODEX_SERVICE_EXEC]: 'Codex Service Exec',
  [AgentClientApplication.CODEX_DESKTOP_APP]: 'Codex Desktop App',
  [AgentClientApplication.CODEX_UNKNOWN_DEFAULT]: 'Codex (Unknown)',
};

const CODEX_CLIENT_APPLICATION_VALUES = new Set<string>(Object.keys(CODEX_CLIENT_APPLICATION_DISPLAY_NAMES));

/**
 * Returns the display name for a client application string.
 * Uses canonical inline display names for values that exist in InlineClientApplication,
 * falls back to beautifyCamelCaseString for AIDR-only values.
 */
export function getClientApplicationDisplayName(rawClientApplication: string | null | undefined): string {
  if (!rawClientApplication) return '';
  const clientApplication = rawClientApplication.trim();
  if (!clientApplication) return '';
  if (CODEX_CLIENT_APPLICATION_DISPLAY_NAMES[clientApplication]) {
    return CODEX_CLIENT_APPLICATION_DISPLAY_NAMES[clientApplication];
  }
  if (INLINE_CLIENT_APPLICATION_VALUES.has(clientApplication)) {
    return getInlineClientApplicationDisplayName(clientApplication as InlineClientApplication);
  }
  if (INLINE_CLIENT_APPLICATION_DISPLAY_NAME_TO_ENUM.has(clientApplication)) {
    return clientApplication;
  }
  return beautifyCamelCaseString(clientApplication);
}

/**
 * Resolves the icon path for a client application string.
 * Handles both camelCase enum values (AIDR pipeline) and display-name strings (inline pipeline).
 */
const CUSTOM_AGENT_VALUES = new Set(['custom agent', 'customagent', 'custom_agent']);

export function isCustomAgentApplication(clientApplication: string | null | undefined): boolean {
  return CUSTOM_AGENT_VALUES.has((clientApplication ?? '').trim().toLowerCase());
}

export function getClientApplicationIconPath(rawClientApplication: string | null | undefined): string {
  if (!rawClientApplication) return 'assets/images/logo/agent-client-app-other.svg';
  const clientApplication = rawClientApplication.trim();
  if (CUSTOM_AGENT_VALUES.has(clientApplication.toLowerCase())) {
    return 'assets/images/logo/agent-client-app-other.svg';
  }
  if (CODEX_CLIENT_APPLICATION_VALUES.has(clientApplication)) {
    return 'assets/images/logo/openAI-codex-icon.svg';
  }
  // Check inline first so shared values (e.g. chatGpt) use the canonical inline icon
  if (INLINE_CLIENT_APPLICATION_VALUES.has(clientApplication)) {
    return getInlineClientApplicationLogoPath(clientApplication as InlineClientApplication);
  }

  if (AGENT_CLIENT_APPLICATION_ICON_VALUES.has(clientApplication)) {
    return `assets/images/logo/agent-client-app-${clientApplication}.svg`;
  }

  const inlineEnum = INLINE_CLIENT_APPLICATION_DISPLAY_NAME_TO_ENUM.get(clientApplication);
  if (inlineEnum) {
    return getInlineClientApplicationLogoPath(inlineEnum);
  }

  return 'assets/images/logo/agent-client-app-other.svg';
}

/**
 * Checks if the agent is a Microsoft 365 agent with a custom name (declarative agent)
 */
export function isM365CustomAgent(aiService: AiService, agentDisplayName: string): boolean {
  return aiService === AiService.m365Copilot && agentDisplayName !== 'Microsoft365Copilot';
}

export function isAiRule(rule: { tags?: string[]; platform?: string }) {
  return rule.tags?.includes(RuleTag.AIDR) || Object.values(AiService).includes(rule.platform as AiService);
}

export function getAgentIconUrl(resourceType?: string, aiService?: AiService): string | null {
  if (aiService) {
    return getAiServiceLogoPath(aiService);
  }

  // Specific case for mcp servers
  if (resourceType === ResourceType.mcp_server) {
    return 'assets/images/logo/mcp-server.svg';
  }

  return null;
}
